import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  RocketLaunch,
  CheckCircle,
  XCircle,
  ArrowsClockwise,
  Globe,
  MagnifyingGlass,
  Clock,
  Copy,
  ArrowRight,
  Warning,
} from "@phosphor-icons/react";
import { deployApi, formatApiError } from "@/lib/api";

// ─── Step icons ───────────────────────────────────────────────────────────────

const STEP_LABELS = {
  add_to_cpanel:     "Add domain to cPanel",
  create_stream:     "Create Adspect stream",
  fetch_filter_php:  "Fetch filter.php from Adspect",
  upload_filter_php: "Upload hashed PHP file",
  upload_index_php:  "Upload index.php",
};

function StepRow({ step }) {
  const label = STEP_LABELS[step.step] || step.step;
  const isSuccess = step.status === "success";
  const isSkipped = step.status === "skipped";
  const isManual = step.status === "manual_required";
  const isFailure = step.status === "failure";

  return (
    <div className="flex items-start gap-3 py-2" data-testid={`step-${step.step}`}>
      <div className="mt-0.5 shrink-0">
        {isSuccess ? (
          <CheckCircle size={18} weight="fill" className="text-green-400" />
        ) : isSkipped ? (
          <Warning size={18} weight="fill" className="text-amber-400" />
        ) : isManual ? (
          <Warning size={18} weight="fill" className="text-orange-400" />
        ) : isFailure ? (
          <XCircle size={18} weight="fill" className="text-red-400" />
        ) : (
          <div className="w-[18px] h-[18px] rounded-full border-2 border-muted-foreground/40 animate-spin border-t-foreground" />
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium leading-tight">{label}</p>
        <p className={`text-xs mt-0.5 break-all ${isManual ? "text-orange-400" : "text-muted-foreground"}`}>
          {step.detail}
        </p>
      </div>
    </div>
  );
}

// ─── Result card ──────────────────────────────────────────────────────────────

function ResultCard({ result }) {
  const [copied, setCopied] = useState(false);

  const copySnippet = () => {
    navigator.clipboard.writeText(result.index_snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const hasManualStep = result.steps.some((s) => s.status === "manual_required");

  return (
    <div className="rounded-xl border border-green-500/30 bg-green-500/5 p-5 space-y-4" data-testid="deploy-result-card">
      <div className="flex items-center gap-2 text-green-400">
        <CheckCircle size={20} weight="fill" />
        <span className="font-semibold">Deployment Complete — Files Deployed</span>
      </div>

      {hasManualStep && (
        <div className="rounded-lg border border-orange-500/40 bg-orange-500/10 p-3 space-y-1" data-testid="manual-step-banner">
          <p className="text-sm font-semibold text-orange-400">One manual step required in cPanel</p>
          <p className="text-xs text-orange-300">
            Go to <strong>cPanel → Addon Domains</strong> → add <strong>{result.domain}</strong> with document root{" "}
            <code className="font-mono">public_html/{result.domain}</code>. All PHP files are already uploaded and ready.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 text-sm">
        <ResultItem label="Domain" value={result.domain} />
        <ResultItem label="Platform" value={result.platform} />
        <ResultItem label="Stream Name" value={result.stream_name} className="col-span-2" />
        <ResultItem label="Stream ID" value={result.stream_id} mono className="col-span-2" />
        <ResultItem label="Hash File" value={result.hash_filename} mono />
        <ResultItem label="Document Root" value={result.document_root} mono />
      </div>

      <div className="space-y-1.5">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
          index.php snippet
        </p>
        <div className="flex items-center gap-2 bg-muted/60 rounded-md p-3">
          <code className="text-xs font-mono text-foreground flex-1 break-all">
            {result.index_snippet}
          </code>
          <button
            onClick={copySnippet}
            className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="copy-snippet-btn"
            title="Copy snippet"
          >
            {copied ? <CheckCircle size={15} className="text-green-400" /> : <Copy size={15} />}
          </button>
        </div>
      </div>

      <div className="space-y-1">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-2">
          Deployment Steps
        </p>
        {result.steps.map((s) => (
          <StepRow key={s.step} step={s} />
        ))}
      </div>
    </div>
  );
}

function ResultItem({ label, value, mono, className }) {
  return (
    <div className={className}>
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      <p className={`text-sm font-medium break-all ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}

// ─── History Table ────────────────────────────────────────────────────────────

const PLATFORM_COLORS = {
  Google: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  Meta:   "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
};

function HistoryTable({ history }) {
  if (!history.length) return null;

  return (
    <div className="mt-10" data-testid="deploy-history">
      <h2 className="text-lg font-bold mb-4">
        Deployment History
        <span className="ml-2 text-sm font-normal text-muted-foreground">({history.length})</span>
      </h2>
      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-muted-foreground text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-3">Domain</th>
              <th className="text-left px-4 py-3">Platform</th>
              <th className="text-left px-4 py-3">Stream Name</th>
              <th className="text-left px-4 py-3">Hash File</th>
              <th className="text-left px-4 py-3">Deployed At</th>
            </tr>
          </thead>
          <tbody>
            {history.map((d) => (
              <tr
                key={d.id}
                className="border-b border-border last:border-0 hover:bg-muted/20"
                data-testid={`history-row-${d.id}`}
              >
                <td className="px-4 py-3 font-medium">{d.domain}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${PLATFORM_COLORS[d.platform] || ""}`}>
                    {d.platform}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground truncate max-w-[220px]">{d.stream_name}</td>
                <td className="px-4 py-3 font-mono text-xs">{d.hash_filename}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs">
                  {new Date(d.created_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function QuickDeployPage() {
  const [domains, setDomains] = useState([]);
  const [cpanelAccounts, setCpanelAccounts] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedDomain, setSelectedDomain] = useState("");
  const [domainSearch, setDomainSearch] = useState("");
  const [platform, setPlatform] = useState("Google");
  const [cpanelAccountId, setCpanelAccountId] = useState("");

  const [deploying, setDeploying] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [domainsRes, accountsRes, historyRes] = await Promise.allSettled([
        deployApi.listDomains(),
        deployApi.listCpanelAccounts(),
        deployApi.getHistory(),
      ]);
      if (domainsRes.status === "fulfilled") setDomains(domainsRes.value.data.domains || []);
      if (accountsRes.status === "fulfilled") {
        const accs = accountsRes.value.data.accounts || [];
        setCpanelAccounts(accs);
        if (accs.length === 1) setCpanelAccountId(accs[0].id);
      }
      if (historyRes.status === "fulfilled") setHistory(historyRes.value.data.deployments || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredDomains = domainSearch
    ? domains.filter((d) => d.toLowerCase().includes(domainSearch.toLowerCase()))
    : domains;

  const canDeploy = selectedDomain && platform && cpanelAccountId && !deploying;

  const handleDeploy = async () => {
    if (!canDeploy) return;
    setDeploying(true);
    setResult(null);
    try {
      const res = await deployApi.setupFilter({
        domain: selectedDomain,
        platform,
        cpanel_account_id: cpanelAccountId,
      });
      setResult(res.data);
      toast.success(`${selectedDomain} deployed successfully!`);
      await load(); // refresh history
    } catch (e) {
      const msg = e.response?.data?.detail || formatApiError(e);
      toast.error(msg);
    } finally {
      setDeploying(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div data-testid="quick-deploy-page">
        <PageHeader
          eyebrow="Traffic Filteration"
          title="Quick Deploy"
          description="Add a domain to cPanel, create an Adspect stream, and deploy PHP filter files — all in one click."
          actions={
            <Button variant="outline" size="sm" onClick={load} data-testid="deploy-refresh-btn">
              <ArrowsClockwise size={15} className="mr-1.5" />
              Refresh
            </Button>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* ── Left: Form ──────────────────────────────────────────────── */}
          <div className="space-y-6">
            <div className="rounded-xl border border-border bg-card p-6 space-y-5">
              <h2 className="text-base font-semibold">Configure Deployment</h2>

              {/* Domain picker */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Select Domain
                  <span className="ml-1.5 text-xs text-muted-foreground">
                    ({domains.length} available from Namecheap)
                  </span>
                </label>
                <div className="relative">
                  <MagnifyingGlass
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    className="w-full pl-8 pr-3 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Search domains…"
                    value={domainSearch}
                    onChange={(e) => {
                      setDomainSearch(e.target.value);
                      setSelectedDomain("");
                    }}
                    data-testid="domain-search-input"
                  />
                </div>
                {(domainSearch || selectedDomain) && (
                  <div
                    className="border border-border rounded-md bg-background max-h-48 overflow-y-auto"
                    data-testid="domain-dropdown"
                  >
                    {filteredDomains.length === 0 ? (
                      <p className="text-sm text-muted-foreground px-3 py-2">No domains match.</p>
                    ) : (
                      filteredDomains.slice(0, 40).map((d) => (
                        <button
                          key={d}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors ${
                            selectedDomain === d ? "bg-primary/10 text-primary font-medium" : ""
                          }`}
                          onClick={() => {
                            setSelectedDomain(d);
                            setDomainSearch(d);
                          }}
                          data-testid={`domain-option-${d}`}
                        >
                          {d}
                        </button>
                      ))
                    )}
                  </div>
                )}
                {selectedDomain && (
                  <div className="flex items-center gap-1.5 text-xs text-green-400" data-testid="selected-domain-badge">
                    <CheckCircle size={13} weight="fill" />
                    <span>{selectedDomain}</span>
                  </div>
                )}
              </div>

              {/* Platform toggle */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Platform</label>
                <div className="grid grid-cols-2 gap-2" data-testid="platform-selector">
                  {["Google", "Meta"].map((p) => (
                    <button
                      key={p}
                      className={`py-2.5 rounded-lg text-sm font-medium border transition-all ${
                        platform === p
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border bg-transparent text-muted-foreground hover:bg-muted/50"
                      }`}
                      onClick={() => setPlatform(p)}
                      data-testid={`platform-${p.toLowerCase()}`}
                    >
                      {p === "Google" ? "Google Ads" : "Meta / Facebook"}
                    </button>
                  ))}
                </div>
              </div>

              {/* cPanel account */}
              <div className="space-y-2">
                <label className="text-sm font-medium">cPanel Account</label>
                {cpanelAccounts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No cPanel accounts configured. Add one in the cPanel section.
                  </p>
                ) : (
                  <Select value={cpanelAccountId} onValueChange={setCpanelAccountId}>
                    <SelectTrigger data-testid="cpanel-account-select">
                      <SelectValue placeholder="Select cPanel account…" />
                    </SelectTrigger>
                    <SelectContent>
                      {cpanelAccounts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name} — {a.hostname}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Stream preview */}
              {selectedDomain && (
                <div className="rounded-md bg-muted/40 border border-border p-3 space-y-1 text-xs">
                  <p className="text-muted-foreground font-medium uppercase tracking-wide">Stream will be created as</p>
                  <p className="font-mono text-foreground" data-testid="stream-name-preview">
                    {platform}_{selectedDomain}_{new Date().toISOString().split("T")[0]}
                  </p>
                  <p className="text-muted-foreground mt-2">Settings: Filter mode · Medium · US only · JS FP off</p>
                </div>
              )}

              {/* Deploy button */}
              <Button
                className="w-full"
                size="lg"
                onClick={handleDeploy}
                disabled={!canDeploy}
                data-testid="deploy-btn"
              >
                {deploying ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Deploying…
                  </>
                ) : (
                  <>
                    <RocketLaunch size={17} className="mr-2" />
                    Deploy
                    {selectedDomain && (
                      <span className="ml-1.5 opacity-70 font-normal text-xs">→ {selectedDomain}</span>
                    )}
                  </>
                )}
              </Button>
            </div>

            {/* What happens box */}
            <div className="rounded-xl border border-border bg-card/50 p-5 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                What happens when you deploy
              </p>
              {[
                "Domain added to selected cPanel account as addon domain",
                `Adspect stream created (${platform} preset, US, filter.php integration)`,
                "filter.php fetched and uploaded as a hashed PHP file",
                "index.php created with require statement pointing to hashed file",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-muted text-xs flex items-center justify-center font-medium text-foreground">
                    {i + 1}
                  </span>
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: Result ────────────────────────────────────────────── */}
          <div>
            {deploying && !result && (
              <div className="rounded-xl border border-border bg-card p-6 space-y-3" data-testid="deploying-indicator">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  Running deployment pipeline…
                </div>
                {["add_to_cpanel", "create_stream", "fetch_filter_php", "upload_filter_php", "upload_index_php"].map((s) => (
                  <div key={s} className="flex items-center gap-3 opacity-40">
                    <div className="w-4 h-4 rounded-full border-2 border-muted-foreground shrink-0" />
                    <span className="text-sm">{STEP_LABELS[s]}</span>
                  </div>
                ))}
              </div>
            )}

            {result && <ResultCard result={result} />}

            {!deploying && !result && (
              <div className="rounded-xl border border-dashed border-border p-16 text-center text-muted-foreground" data-testid="deploy-empty-state">
                <RocketLaunch size={40} className="mx-auto mb-3 opacity-20" />
                <p className="text-sm">Configure the form and click Deploy to run the pipeline.</p>
              </div>
            )}
          </div>
        </div>

        {/* History */}
        <HistoryTable history={history} />
      </div>
    </Layout>
  );
}
